import { AppInsightsCore, IPlugin, INotificationListener, IExtendedConfiguration as IInternalConfiguration } from '@ms/1ds-core-js';
import { PostChannel, IChannelConfiguration } from '@ms/1ds-post-js';

/**
 * The IExtendedConfiguration interface holds the configuration details passed to core during initialize.
 */
export interface IExtendedConfiguration extends IInternalConfiguration {
  /**
   * [Optional] Configuration for the Post Channel to the OneCollector.
   */
  channelConfiguration?: IChannelConfiguration;
}

export class ApplicationInsights extends AppInsightsCore {
  private _postChannel?: PostChannel;

  /**
   * Initialize the sdk.
   * @param {IExtendedConfiguration} config - The configuration to initialize the SDK.
   * @param {IPlugin[]} extensions          - An array of extensions that are to be used by the core.
   */
  initialize(config: IExtendedConfiguration, extensions?: IPlugin[]): void {
    this._postChannel = new PostChannel();
    let plugins: IPlugin[] = [];
    if (extensions) {
      plugins = plugins.concat(extensions);
    }
    config.channels = [[this._postChannel]];
    config.extensionConfig = config.extensionConfig || [];
    config.extensionConfig[this._postChannel.identifier] = config.channelConfiguration ? config.channelConfiguration : {};

    try {
      super.initialize(config, plugins);
    } catch (error) {
      this.logger.warnToConsole('Failed to initialize SDK.' + error);
    }
  }

  setUploadFrequency(uploadFrequency?: number) {
    if (!this._postChannel || !uploadFrequency) {
      return;
    }

    // OneDS SDK allows to specify "Latency" of the event (think priority). At the moment the SDK supports two meaningful latencies
    // (three if you look at the enum definition, but the third one is not used) - EventLatency.Normal (default) and EventLatency.RealTime.
    // The idea here is to upload events with RealTime latency at a higher frequency than the events with Normal latency. To determine the
    // upload frequency for the event, OneDS uses the "concept" of transmission profile. A transmission profile is simply a name that is
    // associated with an array of two numbers - { 'Name' = [Normal, RealTime] }. OneDS documentation specifies that each latency timer
    // needs to be a multiple of the latency higher than it - more on this later.
    // There are three built in profiles:
    //  - RT_PROFILE     = [2, 1]; (default)
    //  - NRT_PROFILE    = [6, 3];
    //  - BE_PROFILE     = [18, 9];
    // Now here is the fun part, only the RealTime latency frequency is used in the code - the code simply doubles the RealTime latency
    //  frequency and uses it for Normal latency frequency - i.e. Normal = RealTime * 2 (see PostChannel._batchAndSendEvents for details).
    // OTel SDK does not expose RealTime latency and uses the default Normal one. So the upload frequency that we are interested here is
    // the Normal one, and to keep things sane for the consumers of the SDK that's what the uploadFrequency parameter will be mapped to.
    let normalLatencyFrequency = uploadFrequency / 1000;
    let realtimeLatencyFrequency = normalLatencyFrequency / 2;

    let customProfiles: { [profileName: string]: number[] } = {};
    customProfiles['OTelCustomTransmissionProfile'] = [normalLatencyFrequency, realtimeLatencyFrequency];

    this._postChannel._loadTransmitProfiles(customProfiles);
    this._postChannel._setTransmitProfile('OTelCustomTransmissionProfile');
  }

  connectNotificationListener(notificationListener?: INotificationListener) {
    if (!notificationListener) {
      return;
    }

    this.addNotificationListener(notificationListener);
  }

  public flush(async: boolean) {
    if (this._postChannel) {
      this._postChannel.flush(async);
    }
  }

  public shutdown() {
    if (this._postChannel) {
      this._postChannel.teardown();
    }
  }
}
